import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { useProjectStore } from '../project/project-store';
import type { LeaseTract } from '../types/api';

export const leaseTractsKeys = {
  all: ['lease-tracts'] as const,
  list: (projectId: string | null, filters: ListFilters) =>
    [...leaseTractsKeys.all, 'list', projectId, filters] as const,
  byId: (projectId: string | null, id: string) =>
    [...leaseTractsKeys.all, 'byId', projectId, id] as const,
};

export interface ListFilters {
  q?: string | undefined;
  /** Filter to one lease's tracts — `GET /lease-tracts?lease_id=<id>`. */
  lease_id?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

export interface UseLeaseTractsOptions {
  /**
   * Extra gate ANDed with the usual `!!projectId` check — used by
   * LeaseTractSection to keep the `?lease_id=` fetch disabled while the
   * owning lease is unsaved (id null), same "disabled-until-saved" contract
   * as useXrefs/useObligationAssignments/useSharedAcreage. Defaults to true
   * (unfiltered project-wide callers, e.g. SharedAcreageSection's candidate
   * list, are unaffected).
   */
  enabled?: boolean | undefined;
}

export function useLeaseTracts(filters: ListFilters = {}, options: UseLeaseTractsOptions = {}) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: leaseTractsKeys.list(projectId, filters),
    enabled: !!projectId && (options.enabled ?? true),
    queryFn: async () => {
      const res = await api.get<{ lease_tracts: LeaseTract[] }>('/lease-tracts', { ...filters });
      return res.lease_tracts;
    },
  });
}

export function useLeaseTract(id: string | null) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: id ? leaseTractsKeys.byId(projectId, id) : ['lease-tracts', 'none'],
    enabled: !!id && !!projectId,
    queryFn: async () => {
      const res = await api.get<{ lease_tract: LeaseTract }>(`/lease-tracts/${id}`);
      return res.lease_tract;
    },
  });
}

export interface LeaseTractInput {
  lease_id: string;

  agreement_number: string;
  type?: string | null | undefined;
  status?: string | null | undefined;
  reviewer_user_id?: string | null | undefined;

  lessor?: string | null | undefined;
  lessee?: string | null | undefined;

  agreement_date?: string | null | undefined;
  expiration_date?: string | null | undefined;

  book?: string | null | undefined;
  page?: string | null | undefined;
  county?: string | null | undefined;
  state?: string | null | undefined;

  legal_description?: string | null | undefined;
  legal_remark?: string | null | undefined;
  all_depths?: string | null | undefined;

  gross_acres?: number | null | undefined;
  net_acres?: number | null | undefined;

  royalty?: number | null | undefined;
  orri_owner?: string | null | undefined;
  orri_decimal?: number | null | undefined;
  wi_owner?: string | null | undefined;
  wi_decimal?: number | null | undefined;
  other_burdens?: string | null | undefined;
}

export function useCreateLeaseTract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LeaseTractInput) =>
      api.post<{ lease_tract: LeaseTract }>('/lease-tracts', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: leaseTractsKeys.all }),
  });
}

// lease_id is deliberately excluded from the patch shape — it's context set
// once at create time, never reassignable (mirrors api/src/routes/lease-
// tracts.ts's updateSchema, which omits lease_id from baseObject.partial()).
export type LeaseTractPatch = Partial<Omit<LeaseTractInput, 'lease_id'>>;

export function useUpdateLeaseTract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: LeaseTractPatch }) =>
      api.patch<{ lease_tract: LeaseTract }>(`/lease-tracts/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: leaseTractsKeys.all }),
  });
}

export function useDeleteLeaseTract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/lease-tracts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: leaseTractsKeys.all }),
  });
}
